import { readSpoField } from "../library-auth-utils"
import {
  isSharePointFamily,
  LIBRARY_ID_PATTERN,
  type LibraryEditorErrors,
  type LibraryEditorMode,
  type LibraryEditorValues,
  OUTPUT_CONTAINER_PATTERN,
  OUTPUT_TABLE_PATTERN,
} from "./library-editor-model"

/** Minimal translator shape so the validator stays testable without i18next. */
export type Translate = (key: string, defaultValue: string) => string

export function validateEditorValues(
  values: LibraryEditorValues,
  modeOrOptions: LibraryEditorMode | { mode: LibraryEditorMode; filthUsesPerUserSpaces?: boolean },
  t: Translate
): LibraryEditorErrors {
  const mode = typeof modeOrOptions === "string" ? modeOrOptions : modeOrOptions.mode
  const filthUsesPerUserSpaces =
    typeof modeOrOptions !== "string" && modeOrOptions.filthUsesPerUserSpaces === true
  const errors: LibraryEditorErrors = {}
  const isFilth = values.search_backend === "filth"

  if (!values.display_name.trim()) {
    errors.display_name = t(
      "adminDashboard.libraries.editor.errors.displayNameRequired",
      "Display name is required"
    )
  }

  if (mode === "create") {
    const id = values.id.trim()
    if (!id) {
      errors.id = t("adminDashboard.libraries.editor.errors.idRequired", "Library ID is required")
    } else if (!LIBRARY_ID_PATTERN.test(id)) {
      errors.id = t(
        "adminDashboard.libraries.editor.errors.idInvalid",
        "ID must contain only lowercase letters, numbers, and hyphens"
      )
    }

    if (!isFilth && !values.embedding_model_id) {
      errors.embedding_model_id = t(
        "adminDashboard.libraries.editor.errors.embeddingModelRequired",
        "Embedding model is required"
      )
    }

    if (values.output_table && !OUTPUT_TABLE_PATTERN.test(values.output_table)) {
      errors.output_table = t(
        "adminDashboard.libraries.editor.errors.outputTableInvalid",
        "Table name must be alphanumeric only and start with a letter"
      )
    }

    if (
      values.output_container &&
      (!OUTPUT_CONTAINER_PATTERN.test(values.output_container) ||
        values.output_container.length < 3)
    ) {
      errors.output_container = t(
        "adminDashboard.libraries.editor.errors.outputContainerInvalid",
        "Container name must be lowercase alphanumeric with hyphens, no leading/trailing hyphens, min 3 chars"
      )
    }

    if (!isFilth && isSharePointFamily(values.source_type)) {
      const url =
        typeof values.source_config.sharepoint_url === "string"
          ? values.source_config.sharepoint_url.trim()
          : ""
      if (!url) {
        errors.sharepoint_url = t(
          "adminDashboard.libraries.editor.errors.sharepointUrlRequired",
          "SharePoint URL is required"
        )
      } else if (
        !values.source_config.hostname ||
        !readSpoField(values.source_config, "siteName")
      ) {
        errors.sharepoint_url = t(
          "adminDashboard.libraries.editor.errors.invalidUrlFormat",
          "Invalid SharePoint URL format. Expected: https://tenant.sharepoint.com/sites/SiteName/LibraryName"
        )
      }
    }

    if (!isFilth && values.source_type === "blob") {
      const container = values.source_config.input_container
      if (typeof container !== "string" || !container.trim()) {
        errors.input_container = t(
          "adminDashboard.libraries.editor.errors.containerRequired",
          "Container name is required"
        )
      }
    }

    if (!isFilth && values.source_type === "dynamics") {
      if (
        typeof values.source_config.environment_url !== "string" ||
        !values.source_config.environment_url.trim()
      ) {
        errors.environment_url = t(
          "adminDashboard.libraries.editor.errors.environmentUrlRequired",
          "Environment URL is required"
        )
      }
      if (
        typeof values.source_config.entity_name !== "string" ||
        !values.source_config.entity_name.trim()
      ) {
        errors.entity_name = t(
          "adminDashboard.libraries.editor.errors.entityNameRequired",
          "Entity name is required"
        )
      }
    }
  }

  if (
    values.filth_source_uids.length > 64 ||
    (values.filth_source_uids.length > 0 && (!isFilth || !filthUsesPerUserSpaces))
  ) {
    errors.filth_source_uids = t(
      "adminDashboard.libraries.editor.sources.invalid",
      "Select at most 64 sources on a primary Prism backend."
    )
  }

  if (isFilth) {
    if (!values.filth_backend_id) {
      errors.filth_backend_id = t(
        "adminDashboard.libraries.editor.errors.filthBackendRequired",
        "Select a Prism backend"
      )
    } else if (!filthUsesPerUserSpaces && !values.company_wide) {
      errors.filth_backend_id = t(
        "adminDashboard.libraries.editor.errors.filthCompanyWide",
        "Legacy and shadow Prism libraries must be marked company-wide"
      )
    }
  }

  if (values.chunk_size !== null && (values.chunk_size < 100 || values.chunk_size > 100000)) {
    errors.chunk_size = t(
      "adminDashboard.libraries.editor.errors.chunkSizeRange",
      "Chunk size must be between 100 and 100000 characters"
    )
  }

  return errors
}

/** Which section owns each error, so the form can open the right one on submit. */
export function sectionForError(
  field: keyof LibraryEditorErrors
): "basics" | "source" | "extraData" | "indexing" {
  switch (field) {
    case "id":
    case "display_name":
    case "filth_backend_id":
    case "filth_source_uids":
      return "basics"
    case "sharepoint_url":
    case "input_container":
    case "environment_url":
    case "entity_name":
      return "source"
    case "extra_data_definition":
      return "extraData"
    default:
      return "indexing"
  }
}
